import { useId } from "react"

import { Alert, AlertDescription, AlertTitle } from "@coval/ui/alert"
import { Button } from "@coval/ui/button"
import { Card, CardContent } from "@coval/ui/card"
import { FormActions, FormField } from "@coval/ui/form-field"
import { Input } from "@coval/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"
import { SettingRow } from "@coval/ui/setting-row"
import { Skeleton } from "@coval/ui/skeleton"
import { Switch } from "@coval/ui/switch"

import { SaveResourceButton } from "@/components/ResourceActionButton"

import {
  useOverageSettingsEditor,
  type OverageSettingsPort,
} from "../hooks/useOverageSettingsEditor"
import {
  currencyFractionDigits,
  minorAmountInput,
} from "../utils/overageSettings"

interface OverageSettingsPanelProps {
  organizationId: string
  port: OverageSettingsPort
}

const LIMIT_DESCRIPTIONS = {
  unset: "No spending limit selected. Choose an amount or Unlimited.",
  unlimited: "No cap on overage spending.",
  limited: "A limit of 0 leaves no budget for paid overages.",
}

export function OverageSettingsForm({
  organizationId,
  port,
  onReviewTerms,
}: OverageSettingsPanelProps & { onReviewTerms?: () => void }) {
  const editor = useOverageSettingsEditor(organizationId, port)
  const reviewTermsHelpId = useId()
  const unsavedChangesReason = editor.isReading
    ? "Wait for your settings to finish loading before reviewing the terms."
    : editor.blocked
      ? "Reload current settings before reviewing the terms."
      : "Save your changes before reviewing the terms."
  const reviewTermsDisabledReason = editor.isSaving
    ? "Wait for your settings to finish saving before reviewing the terms."
    : editor.pendingRequest !== null
      ? "Retry your previous save before reviewing the terms."
      : editor.hasChanges
        ? unsavedChangesReason
        : null
  const { data, draft, feedback } = editor
  const controls = data?.controls
  const fractionDigits = controls
    ? currencyFractionDigits(controls.currency)
    : null
  const usesMinorUnits = fractionDigits === null

  return (
    <div className="space-y-5">
      {onReviewTerms && (
        <div className="space-y-2">
          <Button
            variant="secondary"
            disabled={reviewTermsDisabledReason !== null}
            aria-describedby={
              reviewTermsDisabledReason !== null ? reviewTermsHelpId : undefined
            }
            onClick={onReviewTerms}
          >
            Review paid-overage terms
          </Button>
          {reviewTermsDisabledReason !== null && (
            <p id={reviewTermsHelpId} className="text-sm text-coval-text-muted">
              {reviewTermsDisabledReason}
            </p>
          )}
        </div>
      )}
      {feedback === "load-error" && (
        <Alert variant="danger">
          <AlertTitle>Couldn't load current settings</AlertTitle>
          <AlertDescription>
            Your changes are kept. Reload your settings before saving.
          </AlertDescription>
          <Button
            variant="secondary"
            className="mt-3"
            disabled={editor.isReading}
            onClick={() => void editor.load()}
          >
            Reload settings
          </Button>
        </Alert>
      )}
      {data === null && feedback !== "load-error" && (
        <div
          role="status"
          aria-label="Loading overage settings"
          className="space-y-3"
        >
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {data !== null && !data.configured && (
        <Alert variant="info">
          <AlertTitle>Overages aren't set up</AlertTitle>
          <AlertDescription>
            Contact your Coval account team to set up overage pricing and
            billing currency.
          </AlertDescription>
          <Button
            variant="secondary"
            className="mt-3"
            disabled={editor.isReading}
            onClick={() => void editor.load()}
          >
            Check setup
          </Button>
        </Alert>
      )}
      {controls && draft && (
        <form
          role="presentation"
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            void editor.save()
          }}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              event.target instanceof HTMLInputElement &&
              editor.validationIssues.length > 0
            ) {
              event.preventDefault()
              editor.revealValidation()
            }
          }}
        >
          <div className="rounded-md bg-surface-1 p-3 text-sm text-coval-text-muted">
            <p>
              Billing currency:{" "}
              <span className="font-medium text-coval-text">
                {controls.currency}
              </span>
            </p>
            <p>
              Saved preference: {controls.overages_enabled ? "on" : "off"} ·{" "}
              {controls.limit_state === "limited" &&
              controls.limit_minor !== null
                ? `${controls.currency} ${minorAmountInput(controls.limit_minor, controls.currency)}${usesMinorUnits ? " minor units" : ""}`
                : controls.limit_state === "unset"
                  ? "limit not set"
                  : "unlimited"}
            </p>
          </div>
          <SettingRow
            label="Allow overages when available"
            description="Turning this off keeps your spending limit."
          >
            {(id, describedBy) => (
              <Switch
                id={id}
                {...describedBy}
                checked={draft.overagesEnabled}
                disabled={editor.blocked}
                onCheckedChange={(overagesEnabled) =>
                  editor.changeDraft({ ...draft, overagesEnabled })
                }
              />
            )}
          </SettingRow>
          <SettingRow
            label="Spending limit"
            stacked
            description={LIMIT_DESCRIPTIONS[draft.limitState]}
          >
            {(id, describedBy) => (
              <Select
                value={draft.limitState}
                disabled={editor.blocked}
                onValueChange={(value) => {
                  if (
                    value === "unset" ||
                    value === "unlimited" ||
                    value === "limited"
                  ) {
                    editor.changeDraft({ ...draft, limitState: value })
                  }
                }}
              >
                <SelectTrigger
                  id={id}
                  {...describedBy}
                  aria-label="Spending limit"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Not set</SelectItem>
                  <SelectItem value="unlimited">Unlimited</SelectItem>
                  <SelectItem value="limited">Limited amount</SelectItem>
                </SelectContent>
              </Select>
            )}
          </SettingRow>
          {draft.limitState === "limited" && (
            <FormField
              label={`Limit amount (${controls.currency}${usesMinorUnits ? " minor units" : ""})`}
              description={
                usesMinorUnits
                  ? "Enter an exact whole number of minor units. Currency decimal formatting is unavailable."
                  : `Enter an exact ${controls.currency} amount${fractionDigits === 0 ? " with no decimal places" : ` with up to ${fractionDigits} decimal places`}.`
              }
              error={editor.amountError}
            >
              {(ids) => (
                <Input
                  {...ids}
                  inputMode={
                    usesMinorUnits || fractionDigits === 0
                      ? "numeric"
                      : "decimal"
                  }
                  autoComplete="off"
                  maxLength={40}
                  value={draft.amount}
                  disabled={editor.blocked}
                  onChange={(event) =>
                    editor.changeDraft({
                      ...draft,
                      amount: event.target.value,
                    })
                  }
                />
              )}
            </FormField>
          )}
          {feedback === "conflict" && (
            <Alert variant="warning">
              <AlertTitle>Settings changed elsewhere</AlertTitle>
              <AlertDescription>
                Your changes haven't been saved. Reload the latest settings
                before editing.
              </AlertDescription>
              <Button
                variant="secondary"
                className="mt-3"
                onClick={() => void editor.load()}
              >
                Discard changes and reload
              </Button>
            </Alert>
          )}
          {feedback === "not-configured" && (
            <Alert variant="warning">
              <AlertTitle>Overage setup is unavailable</AlertTitle>
              <AlertDescription>
                Reload your settings to check whether overages are set up for
                your organization.
              </AlertDescription>
              <Button
                variant="secondary"
                className="mt-3"
                onClick={() => void editor.load()}
              >
                Reload settings
              </Button>
            </Alert>
          )}
          {feedback === "uncertain" && (
            <Alert variant="warning">
              <AlertTitle>Couldn't confirm your save</AlertTitle>
              <AlertDescription>
                Your changes are kept while we check. Try the save again.
              </AlertDescription>
              <Button
                variant="secondary"
                className="mt-3"
                disabled={editor.isSaving}
                onClick={() => void editor.save()}
              >
                Try saving again
              </Button>
            </Alert>
          )}
          {feedback === "rejected" && (
            <Alert variant="danger">
              <AlertTitle>Settings were not saved</AlertTitle>
              <AlertDescription>
                Check your access and the values you entered, then try saving
                again.
              </AlertDescription>
            </Alert>
          )}
          {(feedback === "saved" || feedback === "superseded") && (
            <p role="status" className="text-sm text-coval-text-muted">
              {feedback === "saved"
                ? "Settings saved."
                : "Settings saved. A newer change is shown."}
            </p>
          )}
          <FormActions>
            <SaveResourceButton
              type="submit"
              resourceName="overage settings"
              loading={editor.isSaving}
              disabled={editor.blocked || !editor.hasChanges}
              disabledReason={
                editor.pendingRequest
                  ? "Check the previous save before making more changes."
                  : editor.blocked
                    ? "Load and review current settings before saving."
                    : !editor.hasChanges
                      ? "Make a change before saving."
                      : undefined
              }
              validationIssues={editor.validationIssues}
              onValidationAttempt={editor.revealValidation}
            />
          </FormActions>
        </form>
      )}
    </div>
  )
}

export function OverageSettingsPanel(props: OverageSettingsPanelProps) {
  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-coval-text">
            Overage settings
          </h2>
          <p className="text-sm text-coval-text-muted">
            Choose your overage preference and spending limit. Paid overages are
            not active yet.
          </p>
        </div>
        <OverageSettingsForm key={props.organizationId} {...props} />
      </CardContent>
    </Card>
  )
}
